import React from 'react';
import Switch from '@mui/material/Switch';

type ResultRow = {
  type: string;
  code: string;
  rate: string;
  // extra meta is allowed and ignored by the simple table renderer
  [k: string]: any;
};

type Candidate = {
  label: string;
  codeDigits: string; // digits only, used as key
  codePretty: string; // formatted, e.g., "9903.88.05"
  ratePct: number; // numeric percentage (e.g., 10 for 10%)
  rateDisplay: string; // e.g., "10%"
  priority: number; // lower is stronger
  excludedByDigits: string[];
  // These flags are now optional; either may be present depending on source
  isExclusionLabel?: boolean;
  requiresUserChoice?: boolean;
  bd_offer_choice?: boolean;
};

interface ITariffResultsTableProps {
  tariffResults: ResultRow[] | undefined | null;
}

// `id` and `effect` let other claims use the same toggle table (e.g. Canada
// remission orders): `id` keys the toggle when `code` isn't unique, and
// `effect` replaces the Free / Removes MPF label.
export type FtaOption = {
  code: string;
  name: string;
  mpfOnly?: boolean;
  id?: string;
  effect?: string;
};

type Props = {
  options?: FtaOption[];
  enabled?: Record<string, boolean>;
  onToggle?: any;
};

export const TariffResultsTable: React.FC<ITariffResultsTableProps> = ({
  tariffResults,
}) => {
  if (!tariffResults || !tariffResults.length) {
    return (
      <table className='tariff-table'>
        <tbody>
          <tr>
            <td
              className='tariff-col-type'
              colSpan={3}
              style={{ opacity: 0.7 }}
            >
              No items
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className='tariff-table'>
      <tbody>
        {tariffResults.map((item, i) => (
          <tr key={`${item.code || item.type}-${i}`}>
            <td className='tariff-col-type'>
              {item.type}
              {/* Optional caveat under the measure name — used by the Canada
                  surtax rows to say when a charge is conditional, which CAD
                  field it is accounted in, or that precedence did not
                  resolve. Rows without a note render exactly as before. */}
              {item.note ? (
                <div
                  className={`tariff-row-note${
                    item.noteClass ? ` ${item.noteClass}` : ''
                  }`}
                >
                  {item.note}
                </div>
              ) : null}
            </td>
            <td className='tariff-col-code'>{item.code}</td>
            <td className='tariff-col-rate'>{item.rate}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const TariffExclusionsTable: React.FC<{
  candidates: Candidate[] | undefined;
  enabled: Record<string, boolean>;
  onToggle: (codeDigits: string, enabled: boolean) => void;
}> = ({ candidates = [], enabled, onToggle }) => {
  const incoming = Array.isArray(candidates) ? candidates : [];

  // If flags are provided, honor them. If not, assume the list was already filtered upstream
  // Saved snapshots already provide the display-ready exclusions pool.
  // Avoid over-filtering here so static tickets don't lose rows.
  const rows = incoming.filter((c) => Boolean(c?.codeDigits));

  if (!rows.length) {
    return (
      <table className='tariff-table'>
        <tbody>
          <tr>
            <td
              className='tariff-col-type'
              colSpan={4}
              style={{ opacity: 0.7 }}
            >
              No potential exclusions
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className='tariff-table'>
      <tbody>
        {rows.map((c, idx) => (
          // codeDigits is not unique: one HTS code can have several exclusion
          // variants. Include the index/label so each variant row is distinct.
          <tr key={`${c.codeDigits}-${idx}`}>
            <td className='tariff-col-type'>{c.label}</td>
            <td className='tariff-col-code'>{c.codePretty}</td>
            <td className='tariff-col-rate'>{c.rateDisplay}</td>
            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Switch
                size='small'
                checked={Boolean(enabled[c.codeDigits])}
                onChange={(e) => onToggle(c.codeDigits, e.target.checked)}
                inputProps={{ 'aria-label': `Toggle ${c.label}` }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const TariffFtaTable: React.FC<Props> = ({
  options = [],
  enabled = {},
  onToggle,
}) => {
  const rows = Array.isArray(options) ? options : [];

  if (!rows.length) {
    return (
      <table className='tariff-table'>
        <tbody>
          <tr>
            <td
              className='tariff-col-type'
              colSpan={4}
              style={{ opacity: 0.7 }}
            >
              No applicable FTAs
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className='tariff-table'>
      <tbody>
        {rows.map((o) => {
          const key = o.id ?? o.code;
          return (
            <tr key={key}>
              <td className='tariff-col-type'>{o.name}</td>
              <td className='tariff-col-code'>{o.code}</td>
              {/* Data-backed FTAs make the base duty Free; country-eligibility
                  (MPF-only) FTAs leave the base alone and just remove the MPF. */}
              <td className='tariff-col-rate'>
                {o.effect ?? (o.mpfOnly ? 'Removes MPF' : 'Free')}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <Switch
                  size='small'
                  checked={Boolean(enabled[key])}
                  onChange={(e) => onToggle?.(key, e.target.checked)}
                  inputProps={{ 'aria-label': `Toggle ${o.name}` }}
                  disabled={!onToggle}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default TariffFtaTable;
